import React, { createContext, useState, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

// Create theme context for app-wide theme management
export const ThemeContext = createContext({ 
  toggleColorMode: () => {},
  mode: 'light' 
});

/**
 * Theme provider component with dark/light mode support
 * Uses #32cd32 as primary color for both modes
 */
export const ThemeProvider = ({ children }) => {
  // State to track current theme mode
  const [mode, setMode] = useState('light');

  // Context value with toggle function
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
      mode,
    }),
    [mode]
  );

  // Create theme with consistent button colors:
  // - Primary (#32cd32): Normal/Submit/Save buttons
  // - Error (red): Cancel/Delete buttons  
  // - Warning (orange): Edit buttons
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: '#32cd32', // #32cd32 - for normal/submit/save buttons
            light: '#66ff66',
            dark: '#228b22',
            contrastText: '#fff',
          },
          secondary: {
            main: '#f5f5f5',
            dark: '#c2c2c2',
            light: '#ffffff',
            contrastText: '#000',
          },
          error: {
            main: '#dc3545', // red - for cancel/delete buttons
            light: '#e57373',
            dark: '#c62828',
            contrastText: '#fff',
          },
          warning: {
            main: '#ff9800', // orange - for edit buttons
            light: '#ffb74d',
            dark: '#f57c00',
            contrastText: '#fff',
          },
          success: {
            main: '#32cd32', // #32cd32 - same as primary for consistency
            light: '#66ff66',
            dark: '#228b22',
            contrastText: '#fff',
          },
          ...(mode === 'dark' && {
            background: {
              default: '#121212',
              paper: '#1e1e1e',
            },
          }),
        },
        components: {
          // Dropdowns/menus must not lock the body scrollbar — the lock makes
          // the main scrollbar vanish and the whole page shift on every open.
          MuiMenu: {
            defaultProps: {
              disableScrollLock: true,
            },
          },
          MuiPopover: {
            defaultProps: {
              disableScrollLock: true,
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                borderRadius: 8,
                fontWeight: 600,
                padding: '8px 20px',
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                },
              },
              containedPrimary: {
                backgroundColor: '#32cd32',
                '&:hover': {
                  backgroundColor: '#228b22',
                },
              },
              containedError: {
                backgroundColor: '#dc3545',
                '&:hover': {
                  backgroundColor: '#c62828',
                },
              },
              containedWarning: {
                backgroundColor: '#ff9800',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#f57c00',
                },
              },
              outlinedPrimary: {
                borderColor: '#32cd32',
                color: '#32cd32',
                '&:hover': {
                  backgroundColor: 'rgba(50, 205, 50, 0.08)',
                  borderColor: '#228b22',
                },
              },
              outlinedError: {
                borderColor: '#dc3545',
                color: '#dc3545',
                '&:hover': {
                  backgroundColor: 'rgba(220, 53, 69, 0.08)',
                  borderColor: '#c62828',
                },
              },
              outlinedWarning: {
                borderColor: '#ff9800',
                color: '#ff9800',
                '&:hover': {
                  backgroundColor: 'rgba(255, 152, 0, 0.08)',
                  borderColor: '#f57c00',
                },
              },
            },
          },
          MuiTableRow: {
            styleOverrides: {
              root: {
                '&.MuiTableRow-hover:hover': {
                  backgroundColor: mode === 'dark' ? 'rgba(50, 205, 50, 0.08)' : 'rgba(50, 205, 50, 0.04)',
                },
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              colorPrimary: {
                backgroundColor: mode === 'dark' ? '#266e26' : '#e8f5e9',
                color: mode === 'dark' ? '#fff' : '#32cd32',
              },
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={colorMode}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
